import React, { useState, useEffect } from 'react';
import { Zap } from 'lucide-react';
import { useAppStore } from '@/store';
import { AbsenceType, AbsenceReason } from '@abs/shared-types';
import { todayISO, formatDisplayDate } from '@abs/shared-logic';
import { Modal, Select, Button, useToast } from '../shared';
import type { SelectOption } from '../shared';

export interface QuickLogModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const TYPE_OPTIONS: SelectOption[] = Object.values(AbsenceType)
  .filter((t) => t !== AbsenceType.Present && t !== AbsenceType.Cancelled)
  .map((t) => ({ value: t, label: t.charAt(0).toUpperCase() + t.slice(1).replace(/_/g, ' ') }));

const REASON_OPTIONS: SelectOption[] = [
  { value: '', label: '— No reason —' },
  ...Object.values(AbsenceReason).map((r) => ({
    value: r,
    label: r.charAt(0).toUpperCase() + r.slice(1).replace(/_/g, ' '),
  })),
];

export function QuickLogModal({ isOpen, onClose }: QuickLogModalProps): React.ReactElement | null {
  const selectedCourseId = useAppStore((s) => s.selectedCourseId);
  const courses = useAppStore((s) => s.courses);
  const logAbsence = useAppStore((s) => s.logAbsence);
  const { addToast } = useToast();

  const [type, setType] = useState<AbsenceType>(AbsenceType.Unexcused);
  const [reason, setReason] = useState('');

  // Reset to the 90% defaults each time the modal opens so the common
  // case is always one tap away.
  useEffect(() => {
    if (isOpen) {
      setType(AbsenceType.Unexcused);
      setReason('');
    }
  }, [isOpen]);

  const course = courses.find((c) => c.id === selectedCourseId);
  const today = todayISO();

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedCourseId) return;
    logAbsence({
      courseId: selectedCourseId,
      date: today,
      type,
      reason: (reason || null) as AbsenceReason | null,
      arrivalTime: null,
      departureTime: null,
      note: '',
    });
    addToast({
      severity: 'success',
      message: `Logged for ${course?.code ?? 'today'} — your budget updates instantly on Overview.`,
    });
    onClose();
  };

  if (!course) return null;

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Log today's absence" size="sm">
      <form className="quick-log" onSubmit={handleSubmit}>
        <div className="quick-log__context">
          <span className="quick-log__course">{course.code} — {course.name}</span>
          <span className="quick-log__date">Today, {formatDisplayDate(today)}</span>
        </div>

        <Select
          label="Absence Type"
          options={TYPE_OPTIONS}
          value={type}
          onChange={(e) => setType(e.target.value as AbsenceType)}
          required
          hint="Unexcused and excused count toward penalties; late and early-departure may convert based on the policy."
        />
        <Select
          label="Reason"
          options={REASON_OPTIONS}
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          hint="Optional tag used by trend analytics; does not change the penalty calculation."
        />

        <p className="quick-log__hint">
          Need arrival/departure times or a note? Use the full form on the Log Absence tab.
        </p>

        <Button type="submit" variant="primary" size="lg">
          <Zap size={18} aria-hidden="true" />
          Log absence for today
        </Button>
      </form>
    </Modal>
  );
}
